import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DownOutlined, SyncOutlined } from '@ant-design/icons'
import {
  Button,
  Divider,
  Dropdown,
  Menu,
  message,
  Space,
  Tooltip,
  Typography,
  Badge,
  Select,
} from 'antd'
import {
  DeleteModal,
  FilterForm,
  IconFont,
  OptionType,
  SvgIcon,
  FilterFormSelect,
} from '@/components'
import {
  useDeleteTask,
  useDeleteTaskExec,
  useGetAreaData,
  useGetAttackPathList,
  useGetGroupList,
  useGetTaskExecList,
  useGetTaskList,
  useStopTask,
  useGetSecurityDeviceList,
} from './utils'
import TaskTable from '@/components/task-table/task-table'
import DetailDrawer from './detail-drawer'
import { RetestModal } from './retest-modal'
import { useNavigate } from 'react-router-dom'
import { CreateReport } from '@/components/create-report'
import { useAddReport } from '@/pages/evaluation/report-task/utils'
import { Message } from '@/components/message'
import { useChangeJobStatus } from '../plan-list/utils'

import { FilterDropdownProps } from 'antd/lib/table/interface'
import { formatDateTime } from '@/utils/date'
import { t } from '@/i18n'

const { Text } = Typography

export interface TSDataType {
  // 异常原因
  exceptReason?: string
  // 执行次数上限
  executeTimesLimit?: number
  // 已执行次数
  executeTimesUsed?: number
  // 首次运行时间
  firstExecuteTime?: string
  // 任务分组
  groupName?: string
  // 任务id
  id?: string
  // 任务类型
  kind?: string
  // 最近结束时间
  latestExecuteTime?: string
  // 任务名称
  name?: string
  // 任务综合评分
  score?: number
  // 状态分布
  statusList?: { status: string; count: number }[]
  // 主任务状态
  taskStatus?: string
  // 子任务id
  subRowKeys: string[]
  // 资产
  assetName: string
  // 优先级
  priority: number
  // 安全域
  bizAreaName: string
  // 设备
  devices: {
    deviceIp: string
    deviceName: string
    deviceType: string
    isOnline: boolean
  }[]
}

export interface TSSubDataType {
  //  客户端
  agent: string
  //  安全域
  areaName: string
  //  用例发送数
  caseSendTotal: number
  //  用例总数
  caseTotal: number
  //  创建时间
  createTime: string
  // 开始时间
  startTime: string
  //  结束时间
  endTime: string
  //  任务执行id
  id: string
  //  任务评分
  score: number
  // 执行状态
  status: string
  // 离线验证任务名称
  taskName: string
  // 是否可以删除
  isRemove: boolean
}

const KindMap = {
  TaskKindImmediately: t('立即'),
  TaskKindBooking: t('预定'),
  TaskKindCycle: t('周期'),
  TaskKindHighFrequency: t('高频'),
}

const statusMap = {
  TaskStatusFinished: { label: t('已完成'), className: 'success' },
  TaskStatusRunning: { label: t('进行中'), className: 'processing' },
  TaskStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskStatusError: { label: t('错误'), className: 'error' },
  TaskStatusPausing: { label: t('停止中'), className: 'warning' },
  TaskStatusPause: { label: t('暂停'), className: 'warning' },
}

const statusResultMap = {
  TaskResultStatusFinished: { label: t('已完成'), className: 'success' },
  TaskResultStatusProcessing: { label: t('进行中'), className: 'processing' },
  TaskResultStatusWaiting: { label: t('等待中'), className: 'waiting' },
  TaskResultStatusInterrupted: { label: t('中断'), className: 'error' },
  TaskResultStatusStopping: { label: t('停止中'), className: 'warning' },
}

const TaskListMain: React.FC = () => {
  const navigate = useNavigate()
  // 获取安全域 （筛选)
  const [area, getArea] = useGetAreaData()
  // 获取分组列表 (筛选)
  const [getGroupList, groupList] = useGetGroupList()
  // // 获取安全设备列表 (筛选)
  const [getSecurityDeviceList, securityDeviceList] = useGetSecurityDeviceList()

  // 获取攻击路径列表 (筛选)
  const [getAttackPathList, attackPathList] = useGetAttackPathList()
  // 暂停/重启任务
  const [changeJobStatus] = useChangeJobStatus()
  // 获取任务列表
  const [getTaskList, getTaskListLoading] = useGetTaskList()
  // 获取任务执行列表
  const [getTaskExecuteList, getTaskExecuteListLoading] = useGetTaskExecList()
  // 任务停止
  const [stopTask] = useStopTask()
  // 主任务删除
  const [deleteTask, deleteTaskLoading] = useDeleteTask()
  // 子任务删除
  const [deleteSubTask, deleteSubTaskLoading] = useDeleteTaskExec()
  // 创建报表
  const [createReport, createLoading] = useAddReport()
  // 任务复测
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 10,
    isFilter: true,
    resultName: undefined as string | undefined,
    groupId: undefined as string | string[] | undefined,
    agentIds: undefined as number[] | undefined,
    areaName: undefined as string | undefined,
    attackPath: undefined as
      | {
          srcIp?: string
          srcAgentId?: string
          dstIp?: string
          dstAgentId?: string
        }
      | undefined,
    sortField: undefined as string | undefined,
    sortOrder: undefined as string | undefined,
  })
  const [total, setTotal] = useState(0)
  const [dataSource, setDataSource] = useState<TSDataType[]>([])
  const [subDataSource, setSubDataSource] = useState<TSSubDataType[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedSubRowKeys, setSelectedSubRowKeys] = useState<string[]>([])
  const [count, setCount] = useState(1)
  const [subTotal, setSubTotal] = useState(0)
  const [groupSelectOpen, setGroupSelectOpen] = useState(false)

  // 任务筛选
  const [taskFormValue, setTaskFormValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })

  // 任务详情抽屉
  const [view, setView] = useState<{ visible: boolean; id: string }>({ visible: false, id: '' })
  // 删除任务弹窗
  const [remove, setRemove] = useState<{ visible: boolean; id: string[]; type: string }>({
    visible: false,
    id: [],
    type: '',
  })
  // 任务复测弹窗
  let [retestVisible, setRetestVisible] = useState({
    visible: false,
    result_id: '',
    task_id: '',
  })
  // 创建报表弹窗
  const [report, setReport] = useState<{
    visible: boolean
    id: string | string[]
    name: string
    multiple?: boolean
    taskList?: { task_name: string; campaign_id: string }[]
    selectItem?: any
  }>({ visible: false, id: '', multiple: false, name: '', taskList: [], selectItem: null })
  // 任务复测弹窗
  const [mainTaskName, setMainTaskName] = useState('')
  const [assetName, setAssetName] = useState('')
  const [tableChangeLoading, setTableChangeLoading] = useState(false)

  const options = [
    {
      label: t('主任务名称'),
      value: 'search',
      type: 'input' as const,
    },
    {
      label: t('子任务名称'),
      value: 'resultName',
      type: 'input' as const,
    },
    {
      label: t('安全设备'),
      value: 'deviceIds',
      type: 'select' as const,
      mode: 'multiple',
      options: securityDeviceList.map(item => ({
        label: `${item.name || '-'}  | ${item.deviceType} | ${item.deviceIp}`,
        value: item.id,
      })),
    },
    {
      label: t('安全域'),
      value: 'areaName',
      type: 'select' as const,
      options: area
        .filter(item => item !== '')
        .map(item => ({
          label: item,
          value: item,
        })),
    },
    {
      label: t('攻击路径'),
      value: 'agentIds',
      type: 'select' as const,
      options: attackPathList.map(item => ({
        label: `${item.srcIp} → ${item.dstIp}`,
        value: `${item.srcIp}-${item.srcAgentId}-${item.dstIp}-${item.dstAgentId}`,
      })),
    },
  ]

  const buildItems = (id, isRemove) => {
    const items = [
      {
        key: 'copy',
        label: (
          <div className="copy-btn" onClick={() => copyData(id)}>
            <span className="text">{t('任务复制')}</span>
          </div>
        ),
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'main')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),
      },
    ]

    return <Menu items={items} />
  }

  const buildSubItems = (
    taskName: string,
    id: string,
    taskId: string,
    status: string,
    isReport: boolean,
    isRemove: boolean,
    isRetest: boolean,
  ) => {
    const items = [
      {
        key: 'report',
        label: (
          <div
            className={isReport ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => isReport && reportData(id, taskName)}
          >
            <span className="text">{t('生成报表')}</span>
          </div>
        ),

        disabled: !isReport,
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && removeData(id, 'sub')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),

        disabled: !isRemove,
      },
    ]

    const item = {
      key: 'retest',
      label: (
        <div
          style={isRetest ? {} : { color: '#ccc' }}
          className={isRetest ? 'download-btn' : 'download-btn-disabled'}
          onClick={() =>
            isRetest && setRetestVisible({ visible: true, result_id: id, task_id: taskId })
          }
        >
          <span className="text">{t('任务复测')}</span>
        </div>
      ),

      disabled: !isRetest,
    }

    items.splice(1, 0, item)

    return <Menu items={items} />
  }

  const columns = [
    {
      title: t('任务名称'),
      dataIndex: 'name',
      key: 'name',
      width: '11%',
      ellipsis: true,
      render: (_, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={record.name} getPopupContainer={() => document.body}>
            <span
              style={{
                maxWidth: 'calc(100% - 24px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </span>
          </Tooltip>
          {record.devices?.length > 0 && (
            <Tooltip
              getPopupContainer={() => document.body}
              overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
              title={
                <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                  <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                  {record.devices.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 5,
                        flexWrap: 'nowrap',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: '1 1 auto',
                          minWidth: 0,
                        }}
                      >
                        {item.deviceType} | {item.deviceName} | {item.deviceIp}
                      </span>
                      <Badge
                        style={{ margin: '0 8px', flexShrink: 0 }}
                        status={item.isOnline ? 'success' : 'default'}
                      />

                      <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {item.isOnline ? t('在线') : t('离线')}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              <SvgIcon
                name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('任务综合评分'),
      dataIndex: 'score',
      key: 'score',
      width: '13%',
      render: ({
        score,
        riskLevel,
        className,
      }: {
        score: number
        riskLevel: string
        className: string
      }) => (
        <span>
          <span className="score">{typeof score === 'number' ? score / 100 : score}</span>
          <span className="divider">|</span>
          <span className={className}>{riskLevel}</span>
        </span>
      ),

      sorter: (a: TSDataType, b: TSDataType) => (a?.score || 0) - (b?.score || 0),
    },
    {
      title: t('运行状态分布'),
      dataIndex: 'statusList',
      key: 'statusList',
      width: '18%',
      render: statusList => (
        <div style={{ display: 'flex' }}>
          {statusList?.map(status => (
            <div key={status.name} className={`status ${status.className}`}>
              <span>{status.name}</span>
              <span className="count">{status.count}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: t('资产'),
      dataIndex: 'email',
      key: 'email',
      width: '18%',
      ellipsis: true,
      render: (_, record) => {
        const srcNatIp = record?.srcAgent?.natIp ? `|${record?.srcAgent?.natIp}` : ''
        const dstNatIp = record?.dstAgent?.natIp ? `|${record?.dstAgent?.natIp}` : ''
        const srcAgent =
          record?.srcAgent?.agentName && record?.srcAgent?.agentIp
            ? `${record.srcAgent.agentName}|${record.srcAgent.agentIp}${srcNatIp}`
            : '-'
        const dstAgent =
          record?.dstAgent?.agentName && record?.dstAgent?.agentIp
            ? `${record.dstAgent.agentName}|${record.dstAgent.agentIp}${dstNatIp}`
            : '-'
        const srcArea = record?.srcAgent?.bizAreaName || '-'
        const dstArea = record?.dstAgent?.bizAreaName || '-'

        const displayText = srcAgent === '-' && dstAgent === '-' ? '-' : `${srcAgent} → ${dstAgent}`

        const areaText = srcArea === '-' && dstArea === '-' ? '-' : `${srcArea} → ${dstArea}`

        return (
          <Tooltip
            title={
              <div>
                <div>{displayText}</div>
                <div>{areaText}</div>
              </div>
            }
            getPopupContainer={() => document.body}
            placement="topLeft"
          >
            <span>{displayText}</span>
          </Tooltip>
        )
      },
    },
    {
      title: t('执行类型'),
      dataIndex: 'kind',
      key: 'kind',
      width: '10%',
      render: (kind: string, { executeTimesUsed, executeTimesLimit }: TSDataType) => (
        <div
          className={
            [t('立即'), t('预定')].includes(kind) ? 'kind-immediately kind-wrapper' : 'kind-wrapper'
          }
        >
          <span className="kind">{kind}</span>
          {![t('立即'), t('预定')].includes(kind) && (
            <>
              <span>{executeTimesUsed || 0}</span>
              <span className="divider">|</span>
              <span>{executeTimesLimit === 0 ? '∞' : executeTimesLimit}</span>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('优先级'),
      dataIndex: 'priority',
      key: 'priority',
      width: '7%',
      ellipsis: true,
      sorter: (a: TSDataType, b: TSDataType) => (a?.priority || 0) - (b?.priority || 0),
    },
    {
      title: t('分组'),
      dataIndex: 'groupId',
      key: 'groupId',
      width: '10%',
      filteredValue: searchParams?.groupId ? searchParams?.groupId : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('请选择分组')}
            value={props.selectedKeys as string[]}
            open={groupSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setGroupSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
              setSearchParams(prev => ({ ...prev, groupId: value, isFilter: true }))
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setGroupSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={groupList.map(type => ({
              label: type.name,
              value: type.id,
            }))}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (_, record) => <span>{record.groupName || '-'}</span>,
    },
    {
      title: t('首次运行时间'),
      dataIndex: 'firstExecuteTime',
      key: 'firstExecuteTime',
      width: '12%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('最近结束时间'),
      dataIndex: 'latestExecuteTime',
      key: 'latestExecuteTime',
      width: '10%',
      ellipsis: true,
      render: text => (
        <Tooltip
          title={text === '-' ? '-' : formatDateTime(text, 'standard')}
          getPopupContainer={() => document.body}
        >
          {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
        </Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: '20%',
      fixed: 'right',
      render: (_, { id, taskStatus, exceptReason, isRemove, name }) => (
        <Space size="middle">
          {taskStatus && (
            <div className="stop-btn">
              {[t('等待中'), t('中断'), t('暂停'), t('错误')].includes(taskStatus) && (
                <span
                  onClick={() => {
                    if (taskStatus === t('中断') || taskStatus === t('错误')) {
                      message.warn({
                        className: 'task-warn',
                        content: (
                          <div>
                            <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
                            {t('异常原因：')}
                            {exceptReason}
                          </div>
                        ),
                      })
                      return
                    }
                    handleOpenTask(id, 2)
                  }}
                >
                  <span className="text">{t('开启')}</span>
                </span>
              )}
              {[t('进行中')].includes(taskStatus) && (
                <span onClick={() => handleOpenTask(id, 1)}>
                  <span className="text">{t('暂停')}</span>
                </span>
              )}
            </div>
          )}
          <div className="view-btn" onClick={() => viewData(id)}>
            <span className="text">{t('查看')}</span>
          </div>
          <Dropdown
            overlay={buildItems(id, isRemove)}
            getPopupContainer={() => document.body}
            overlayStyle={{ zIndex: 9999 }}
          >
            <span style={{ flexShrink: 0 }}>
              {t('更多')} <DownOutlined />
            </span>
          </Dropdown>
        </Space>
      ),
    },
  ]

  const subColumns = useMemo(() => {
    const arr = [
      {
        title: t('子任务名称'),
        dataIndex: 'taskName',
        key: 'taskName',
        width: '20%',
        ellipsis: true,
        render: (_, record) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title={record.taskName} getPopupContainer={() => document.body}>
              <span
                style={{
                  maxWidth: 'calc(100% - 24px)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.taskName}
              </span>
            </Tooltip>
            {record.devices?.length > 0 && (
              <Tooltip
                getPopupContainer={() => document.body}
                overlayStyle={{ maxWidth: 'min(800px, 90vw)' }}
                title={
                  <div style={{ padding: 8, maxWidth: 'min(800px, 90vw)' }}>
                    <div style={{ marginBottom: 4 }}>{t('设备清单')}</div>
                    {record.devices.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 5,
                          flexWrap: 'nowrap',
                          width: '100%',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: '1 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {item.deviceType} | {item.deviceName} | {item.deviceIp}
                        </span>
                        <Badge
                          style={{ margin: '0 8px', flexShrink: 0 }}
                          status={item.isOnline ? 'success' : 'default'}
                        />

                        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {item.isOnline ? t('在线') : t('离线')}
                        </span>
                      </div>
                    ))}
                  </div>
                }
              >
                <SvgIcon
                  name={record.devices.some(d => d.isOnline) ? t('设备') : t('设备灰')}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: t('任务评分'),
        dataIndex: 'score',
        key: 'score',
        width: '16%',
        render: ({
          score,
          riskLevel,
          className,
        }: {
          score: number
          riskLevel: string
          className: string
        }) => (
          <span>
            <span className="score">{typeof score === 'number' ? score / 100 : score}</span>
            <span className="divider">|</span>
            <span className={className}>{riskLevel}</span>
          </span>
        ),
      },
      {
        title: t('运行状态'),
        dataIndex: 'status',
        key: 'status',
        width: '16%',
        render: (status, { caseSendTotal, caseTotal, exceptReason }) => (
          <div className="status-wrapper">
            <div className="plugin-status">
              <span>{caseSendTotal || 0}</span>
              <span className="divider">|</span>
              <span>{caseTotal}</span>
            </div>
            <span className={`status ${status?.className}`}>{status?.label}</span>
            {status?.label === t('中断') && (
              <Tooltip title={exceptReason} getPopupContainer={() => document.body}>
                <IconFont
                  type={'icon-jinggao_jinggao'}
                  style={{ color: '#F53F3F', fontSize: '14px' }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: t('创建时间'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: '10%',
        ellipsis: true,
        render: (text: string) => (
          <Tooltip
            title={text === '-' ? '-' : formatDateTime(text, 'standard')}
            getPopupContainer={() => document.body}
          >
            {text === '-' ? '-' : formatDateTime(text, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('开始时间'),
        dataIndex: 'startTime',
        key: 'startTime',
        width: '10%',
        ellipsis: true,
        render: (startTime: string) => (
          <Tooltip
            title={startTime === '-' ? '-' : formatDateTime(startTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {startTime === '-' ? '-' : formatDateTime(startTime, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('结束时间'),
        dataIndex: 'endTime',
        key: 'endTime',
        width: '10%',
        ellipsis: true,
        render: (endTime: string) => (
          <Tooltip
            title={endTime === '-' ? '-' : formatDateTime(endTime, 'standard')}
            getPopupContainer={() => document.body}
          >
            {endTime === '-' ? '-' : formatDateTime(endTime, 'mdhm')}
          </Tooltip>
        ),
      },
      {
        title: t('操作'),
        key: 'action',
        width: '18%',
        fixed: 'right',
        render: (
          _,
          { taskName, taskId, id, status, isStop, isView, isReport, isRetest, isRemove },
        ) => (
          <Space size="middle">
            {isStop && (
              <div onClick={() => handleStopTask(id, taskId)} className="stop-btn">
                <IconFont type="icon-zanting2" />
                <span style={{ marginLeft: 6 }}>{t('停止')}</span>
              </div>
            )}
            <div
              className={isView ? 'view-btn' : 'view-btn-disabled'}
              onClick={() =>
                isView &&
                window.open(
                  `/validation/network/threatprotection?tab=task&name=detail&id=${id}&task_id=${taskId}`,
                  '_blank',
                )
              }
            >
              <IconFont type="icon-chakan" />
              <span className="text">{t('查看')}</span>
            </div>
            <Dropdown
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 9999 }}
              overlay={buildSubItems(
                taskName,
                id,
                taskId,
                status?.label,
                isReport,
                isRemove,
                isRetest,
              )}
            >
              <span>
                {t('更多')} <DownOutlined />
              </span>
            </Dropdown>
          </Space>
        ),
      },
    ]

    return arr
  }, [subDataSource])

  // 查看任务详情
  function viewData(id: string) {
    setView({ visible: true, id })
  }

  // 关闭任务详情
  function closeView() {
    setView({ visible: false, id: '' })
  }

  // 打开删除弹窗
  function removeData(id: string | string[], type: string) {
    console.log('remove data', id)
    if (typeof id === 'string') {
      setRemove({ visible: true, id: [id], type })
    } else {
      setRemove({ visible: true, id, type })
    }
  }

  function retestData(id: string) {
    console.log('retest data', id)
  }

  function copyData(id: string) {
    navigate('/validation/network/threatprotection?tab=create', {
      state: { id, from: 'taskList' },
    })
  }

  function reportData(id: string, name: string) {
    const taskList = [{ task_name: name, campaign_id: id }]
    setReport({ visible: true, id, name, taskList })
  }

  function closeReport() {
    setReport({ visible: false, id: '', name: '', taskList: [], selectItem: [], multiple: false })
  }

  // 停止子任务
  async function handleStopTask(id: string, taskId: string) {
    try {
      await stopTask({ result_id: id })
      initTableData(taskId, 1, true)
      Message.success({ content: t('任务停止成功'), className: 'task-success' })
    } catch (error) {
      console.log(error)
    }
  }

  // 计划任务暂停/启动
  async function handleOpenTask(id: string, taskStatus: number) {
    try {
      await changeJobStatus({ id: id, task_status: taskStatus })
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      taskStatus === 1
        ? Message.error({ content: t('任务暂停'), className: 'task-error' })
        : Message.info({ content: t('任务启动'), className: 'task-info' })
      loadTableData(id)
    } catch (error: any) {
      if (error.code === 400) {
        message.warn({
          className: 'task-warn',
          content: (
            <span>
              <div style={{ textAlign: 'left' }}>{t('任务异常，无法运行')}</div>
              <div>
                {t('异常原因：')}
                {error.msg}
              </div>
            </span>
          ),
        })
      }
    }
  }

  /**
   * 删除任务
   * @param type 类型，main：主任务，sub：子任务 all：全部
   */
  const handleDelete = async () => {
    try {
      if (remove.type === 'main') {
        await deleteTask(remove.id)
      } else if (remove.type === 'sub') {
        await deleteSubTask({ result_id_list: remove.id })
      } else {
        await deleteTask(selectedRowKeys)
        if (selectedSubRowKeys.length > 0) {
          await deleteSubTask({ result_id_list: selectedSubRowKeys })
        }
      }
      setRemove({ visible: false, id: [], type: '' })
      setSelectedRowKeys([])
      setSelectedSubRowKeys([])
      setSearchParams({ ...searchParams, page: 1, limit: 10 })
    } catch (error) {
      console.log(error)
    }
  }

  // 展开事件
  const handleExpand = ({ expanded, record }) => {
    if (expanded) {
      loadTableData(record.id)
      setMainTaskName(record.name)
      setAssetName(record.assetName)
      setExpandedRowKeys([record.key])
      setCount(1)
    } else {
      setExpandedRowKeys([])
      setSubDataSource([])
      setSelectedSubRowKeys([])
      setCount(1)
    }
  }

  // 创建报表
  const confirmAddGroup = values => {
    let taskData: { task_name: string; campaign_id: number }[] = []
    if (report.multiple) {
      taskData = values.campaign_ids.reduce((acc, id) => {
        const item = report.selectItem.find(i => i.id === id)
        if (item) {
          acc.push({ task_name: item.task_name, campaign_id: item.id })
        }
        return acc
      }, [])
    } else {
      if (report.taskList) {
        taskData = report.taskList
      }
    }

    createReport({
      task_list: taskData,
      report_name: values.report_name,
      template_type:
        report?.multiple && report?.selectItem?.length > 1 ? [2] : values.template_type,
      campaign_type: values.campaign_type,
      emails: '',
      assets: 0,
      include_device_logs: values.include_device_logs,
    })
      .then(() => {
        window.open('/report/task/task-report?tab=5', '_self')
      })
      .catch(_ => {})
  }

  // 构建主任务数据
  const buildDataSource = (data, taskId: string, subRowKeys: string[], isOrigin?: boolean) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))
    const tableData: TSDataType[] = []

    // 仅是更新 subRowKeys
    if (isOrigin) {
      for (let i = 0; i < arr.length; i++) {
        let item = arr[i]
        if (item.id === taskId) {
          item.subRowKeys = subRowKeys
        }

        tableData.push(item)
      }

      return tableData
    }

    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const {
        firstExecuteTime,
        latestExecuteTime,
        id,
        kind,
        statusList,
        taskStatus,
        exceptReason,
        devices,
      } = item
      console.log('kind', kind, taskStatus)
      // 中断的状态是否是唯一的
      const isUnique = !statusList.some(status => status.status !== 'TaskResultStatusInterrupted')
      const score = (
        item.score === 0 ? (isUnique ? '-' : 0) : item.score === 10000 ? 10000 : item.score || 0
      ) as number
      item.firstExecuteTime = firstExecuteTime
        ? new Date(firstExecuteTime).toLocaleString().replace(/\//g, '-')
        : '-'
      item.latestExecuteTime = latestExecuteTime
        ? new Date(latestExecuteTime).toLocaleString().replace(/\//g, '-')
        : '-'
      item.key = id
      item.kind = KindMap[kind]
      item.devices = devices
      item.score = {
        score,
        riskLevel: isUnique
          ? '-'
          : (score <= 10000 && score >= 8000 && t('优秀')) ||
            (score < 8000 && score >= 6000 && t('良好')) ||
            (score < 6000 && score >= 4000 && t('一般')) ||
            (score < 4000 && score >= 2000 && t('较弱')) ||
            t('弱'),

        className:
          (score <= 10000 && score >= 8000 && 'low-risk') ||
          (score < 8000 && score >= 6000 && 'medium-risk') ||
          (score < 6000 && score >= 4000 && 'high-risk') ||
          (score < 4000 && score >= 2000 && 'weaker-risk') ||
          'serious-risk',
      }
      item.statusList = statusList.map(status => ({
        name: statusResultMap[status.status].label,
        count: status.count,
        className: statusResultMap[status.status].className,
      }))
      item.isRemove = statusList.find(status =>
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status.status),
      )
        ? false
        : true

      item.taskStatus =
        kind === 'TaskKindCycle' || kind === 'TaskKindHighFrequency'
          ? statusMap[taskStatus]?.label
          : undefined
      item.exceptReason = exceptReason ? exceptReason : undefined

      if (item.id === taskId) {
        item.subRowKeys = subRowKeys
      }

      tableData.push(item)
    }

    return tableData
  }

  // 构建子任务数据
  const buildSubDataSource = (data, taskId: string) => {
    if (!data || data.length === 0) return []

    const arr = JSON.parse(JSON.stringify(data))
    const subTableData: TSSubDataType[] = []
    for (let i = 0; i < arr.length; i++) {
      let item = arr[i]
      const {
        id,
        taskName,
        status,
        createTime,
        startTime,
        endTime,
        caseSendTotal,
        attackIp,
        defenseIp,
      } = item
      const score = (
        item.score === 0
          ? status !== 'TaskResultStatusFinished'
            ? '-'
            : 0
          : item.score === 100
          ? 100
          : item.score || 0
      ) as number
      item.key = id
      item.taskId = taskId
      item.taskName = taskName
      item.score = {
        score,
        riskLevel:
          status !== 'TaskResultStatusFinished'
            ? '-'
            : (score <= 10000 && score >= 8000 && t('优秀')) ||
              (score < 8000 && score >= 6000 && t('良好')) ||
              (score < 6000 && score >= 4000 && t('一般')) ||
              (score < 4000 && score >= 2000 && t('较弱')) ||
              t('弱'),

        className:
          (score <= 10000 && score >= 8000 && 'low-risk') ||
          (score < 8000 && score >= 6000 && 'medium-risk') ||
          (score < 6000 && score >= 4000 && 'high-risk') ||
          (score < 4000 && score >= 2000 && 'low-risk') ||
          'serious-risk',
      }
      item.status = statusResultMap[status]
      item.agent = {
        attackIp,
        defenseIp,
      }
      item.createTime = new Date(createTime).toLocaleString().replace(/\//g, '-')
      item.startTime = startTime ? new Date(startTime).toLocaleString().replace(/\//g, '-') : '-'
      item.endTime = endTime ? new Date(endTime).toLocaleString().replace(/\//g, '-') : '-'
      item.isReport = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusStopping',
        ].includes(status) ||
        ([
          'TaskResultStatusProcessing',
          'TaskResultStatusInterrupted',
          'TaskResultStatusStopping',
        ].includes(status) &&
          caseSendTotal === 0)
      )

      item.isRetest = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status)
      item.isRemove = ![
        'TaskResultStatusWaiting',
        'TaskResultStatusProcessing',
        'TaskResultStatusStopping',
      ].includes(status)
      item.isView = !(
        [
          'TaskResultStatusWaiting',
          'TaskResultStatusProcessing',
          'TaskResultStatusInterrupted',
          'TaskResultStatusStopping',
        ].includes(status) && caseSendTotal === 0
      )

      item.isStop = ['TaskResultStatusWaiting', 'TaskResultStatusProcessing'].includes(status)

      subTableData.push(item)
    }

    return subTableData
  }

  // 初始化表格数据
  const initTableData = async (taskId?: string, page?: number, onlyRefresh?: boolean) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      if (res.data.total === 0) return setDataSource([])
      const resp = await getTaskExecuteList({
        taskId: taskId || res.data.list?.[0]?.id,
        ...searchParams,
        search: searchParams.resultName ? searchParams.resultName : undefined,
        page: onlyRefresh ? 1 : page || count,
      })
      setSubTotal(resp.data.total)
      if (!mainTaskName) {
        setMainTaskName(res.data.list?.[0]?.name)
      }
      if (!assetName) {
        setAssetName(res.data.list?.[0]?.assetName)
      }
      setSubDataSource(buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id))
      const subRowKeys = buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, taskId || res.data.list?.[0]?.id, subRowKeys))

      if (onlyRefresh) return
      if (resp.data.list?.length > 0) {
        setExpandedRowKeys([taskId || res.data.list?.[0]?.id])
      } else {
        setExpandedRowKeys([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  // 加载表格数据
  const loadTableData = async (id: string) => {
    try {
      const res = await getTaskList(searchParams)
      setTotal(res.data.total)
      let resp
      if (count !== 1) {
        resp = await getTaskExecuteList({
          taskId: id,
          ...searchParams,
          page: 1,
          search: searchParams.resultName ? searchParams.resultName : undefined,
        })
      } else {
        resp = await getTaskExecuteList({
          taskId: id,
          ...searchParams,
          page: count,
          search: searchParams.resultName ? searchParams.resultName : undefined,
        })
      }
      setSubTotal(resp.data.total)
      setSubDataSource(buildSubDataSource(resp.data.list, id))
      const subRowKeys = buildSubDataSource(resp.data.list, id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      setDataSource(buildDataSource(res.data.list, id, subRowKeys))
      setExpandedRowKeys([id])
    } catch (error) {
      console.log(error)
    }
  }

  // 加载更多子任务数据
  const loadMoreSubData = async (id: string) => {
    setCount(origin => origin + 1)
    const resp = await getTaskExecuteList({
      taskId: id,
      ...searchParams,
      page: count + 1,
      search: searchParams.resultName ? searchParams.resultName : undefined,
    })
    setSubDataSource(origin => [...origin, ...buildSubDataSource(resp.data.list, id)])
    const subRowKeys = buildSubDataSource(resp.data.list, id)
      .filter(item => item.isRemove)
      .map(v => v.id)
    setDataSource(origin => [...buildDataSource(origin, id, subRowKeys, true)])
  }

  useEffect(() => {
    if (taskFormValue?.field) {
      const selectedOption = options.find(opt => opt.value === taskFormValue.field)
      if (selectedOption) {
        // 只有当 value 有值时才更新 searchCaseParams

        setSearchParams(prev => {
          if (prev[taskFormValue.field] === taskFormValue.value) {
            return prev
          }

          // 如果值为空字符串或空数组，说明是切换了字段，需要清除所有旧的筛选参数
          if (
            taskFormValue.value === '' ||
            (Array.isArray(taskFormValue.value) && taskFormValue.value.length === 0)
          ) {
            // 只保留分页相关参数
            return {
              page: 1,
              limit: prev.limit,
              isFilter: false,
              resultName: undefined,
              groupId: undefined,
              agentIds: undefined,
              areaName: undefined,
              attackPath: undefined,
              sortField: undefined,
              sortOrder: undefined,
            }
          }

          // 处理攻击路径字段
          if (taskFormValue.field === 'agentIds' && typeof taskFormValue.value === 'string') {
            const parts = taskFormValue.value.split('-')
            if (parts.length === 4) {
              return {
                ...prev,
                page: 1,
                isFilter: true,
                'attackPath.srcIp': parts[0],
                'attackPath.srcAgentId': parts[1],
                'attackPath.dstIp': parts[2],
                'attackPath.dstAgentId': parts[3],
              }
            }
          }

          return {
            ...prev,
            page: 1,
            isFilter: true,
            [taskFormValue.field]: taskFormValue.value,
          }
        })
      }
    }
  }, [taskFormValue])

  useEffect(() => {
    setCount(1)
    initTableData(undefined, 1)

    return () => {
      setTotal(0)
      setDataSource([])
      setSubDataSource([])
    }
  }, [searchParams])

  useEffect(() => {
    getArea().catch(_ => {})
    getGroupList().catch(_ => {})
    getAttackPathList().catch(_ => {})
    getSecurityDeviceList().catch(_ => {})
  }, [])

  const batchOperation = (
    <Space split={<Divider type={'vertical'} />}>
      <div>{t('批量操作')}</div>
      <div className="batch-delete-btn" onClick={() => removeData([], 'all')}>
        <SvgIcon name={'batch_remove'} className="icon-delete" />
        <span>{t('批量删除')}</span>
      </div>
      {selectedRowKeys.length > 1 ? (
        <></>
      ) : (
        selectedSubRowKeys.length > 0 && (
          <div
            className={
              selectedSubRowKeys.length > 5 ? 'batch-report-btn disabled' : 'batch-report-btn'
            }
            onClick={() => {
              if (selectedSubRowKeys.length > 5) return
              const selectItem = [
                ...new Map(
                  subDataSource
                    .filter(item => selectedSubRowKeys.includes(item.id))
                    .map(v => [
                      v.id,
                      {
                        ...v,
                        task_name: v.taskName,
                        result_status:
                          v.status?.label === t('已完成')
                            ? 3
                            : v.status?.label === t('中断')
                            ? 4
                            : 7,
                        executed_plugin_num: v.caseSendTotal,
                      },
                    ]),
                ).values(),
              ]

              setReport({
                visible: true,
                multiple: true,
                name: '',
                id: selectedSubRowKeys,
                selectItem,
              })
            }}
          >
            <SvgIcon
              name={selectedSubRowKeys.length > 0 ? t('批量生成报表') : 'batch_remove_ash'}
              className="icon-export"
            />

            <span>{t('批量生成报表')}</span>
          </div>
        )
      )}
    </Space>
  )

  // 处理表格变化事件
  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    setTableChangeLoading(true)
    console.log('filters', filters)
    try {
      const params = {
        ...searchParams,
        page: pagination.current,
        limit: pagination.pageSize,
      }

      // 处理主表格筛选
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] && filters[key].length > 0) {
            params[key] = filters[key]
          } else {
            delete params[key]
          }
        })
      }

      // 处理主表格排序
      if (sorter && sorter.field) {
        params.sortField =
          sorter.field === 'score'
            ? 'sort_field_score'
            : sorter.field === 'firstExecuteTime'
            ? 'sort_field_update_time'
            : 'sort_field_priority'
        params.sortOrder =
          sorter.order === 'ascend'
            ? 'SORT_ORDER_ASCENDING'
            : sorter.order === 'descend'
            ? 'SORT_ORDER_DESCENDING'
            : undefined
      } else {
        delete params.sortField
        delete params.sortOrder
      }
      if (params.sortField && !params.sortOrder) {
        delete params.sortField
      }
      setSearchParams(params)
    } finally {
      setTableChangeLoading(false)
    }
  }

  // 处理子表格变化事件
  const handleSubTableChange = async (pagination: any, filters: any, sorter: any) => {}

  return (
    <Wrapper>
      {/* 标题 */}
      <div className="title-wrapper">
        <div className="title">
          <span>{t('任务列表')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="search-wrapper" style={{ marginRight: 8 }}>
            <FilterFormSelect
              filterOptions={options}
              value={taskFormValue}
              onChange={newValue => {
                setTaskFormValue(newValue)
              }}
            />
          </div>
          <div className="reset">
            <Button
              icon={<SyncOutlined style={{ color: '#666' }} />}
              className="reset-btn"
              onClick={() => {
                initTableData(expandedRowKeys[0], 1, true)
                setCount(1)
              }}
            />
          </div>
        </div>
      </div>
      {/* 任务列表 */}
      <div className="table-wrapper">
        <TaskTable
          batchOperation={batchOperation}
          columns={columns}
          subColumns={subColumns}
          dataSource={dataSource}
          subDataSource={subDataSource}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          total={total}
          loading={getTaskListLoading}
          hasMore={subTotal > searchParams.limit * count}
          loadMoreSubData={loadMoreSubData}
          subLoading={getTaskExecuteListLoading}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedSubRowKeys={selectedSubRowKeys}
          setSelectedSubRowKeys={setSelectedSubRowKeys}
          expandedRowKeys={expandedRowKeys}
          expandHandler={handleExpand}
          onTableChange={handleTableChange}
          onSubTableChange={handleSubTableChange}
          tableChangeLoading={tableChangeLoading}
        />
      </div>
      {/* 详情抽屉 */}
      <DetailDrawer open={view.visible} onClose={closeView} id={view.id} />
      {/* 删除任务弹窗 */}
      <DeleteModal
        text={
          remove.type === 'main'
            ? t('是否确认删除所选任务（删除主任务时将同时删除其下的子任务）？')
            : t('此操作将永久删除，是否继续？')
        }
        open={remove.visible}
        onOk={() => handleDelete()}
        onCancel={() => setRemove(origin => ({ ...origin, visible: false, type: '' }))}
        loading={
          (remove.type === 'main' && deleteTaskLoading) ||
          (remove.type === 'sub' && deleteSubTaskLoading)
        }
      />

      {/* 创建报表弹窗 */}
      <CreateReport
        visible={report.visible}
        multiple={report.multiple}
        selectItem={report.selectItem}
        createLoading={createLoading}
        templateType={5}
        onConfirm={e => {
          confirmAddGroup(e)
        }}
        onCancel={closeReport}
      />

      {/* 任务复测弹窗 */}
      <RetestModal
        visible={retestVisible.visible}
        onCancel={() => {
          setRetestVisible({ visible: false, result_id: '', task_id: '' })
        }}
        onSuccess={() => {
          setRetestVisible(origin => ({ ...origin, visible: false }))
          initTableData(retestVisible.task_id, 1)
          setCount(1)
        }}
        id={retestVisible.result_id}
        name={mainTaskName}
      />
    </Wrapper>
  )
}

export default TaskListMain

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 6px;

    .title {
      font-weight: 600;
      font-size: 16px;
    }

    .reset-btn {
      width: 32px;
      height: 32px;
      background: #f1f3f8;
      border-radius: 6px;
      border: none;

      &:hover {
        background: #e6e9f0;
        /* border: 1px solid #4238c9; */
      }
    }
  }

  .table-wrapper {
    padding: 0 20px;
    overflow-y: scroll;
  }
`
const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
